import { SystemStyleObject } from '@chakra-ui/react'
import { mode, transparentize } from '@chakra-ui/theme-tools'
import type { SystemStyleFunction } from '@chakra-ui/theme-tools'

const baseStyle: SystemStyleObject = {
  display: 'flex',
  gap: 1,
  lineHeight: 'button',
  borderRadius: 'md',
  fontWeight: 'normal',
  transitionProperty: 'common',
  transitionDuration: 'normal',
  whiteSpace: 'break-spaces',
  textAlign: 'start',
  _focus: {
    boxShadow: 'outline',
  },
  _disabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  _hover: {
    _disabled: {
      bg: 'initial',
    },
  },
}

const variantGhost: SystemStyleFunction = (props) => {
  const { colorScheme: c, theme } = props

  if (c === 'gray') {
    return {
      color: mode(`inherit`, `whiteAlpha.900`)(props),
      _hover: {
        bg: mode(`gray.100`, `whiteAlpha.200`)(props),
      },
      _active: { bg: mode(`gray.200`, `whiteAlpha.300`)(props) },
    }
  }

  const darkHoverBg = transparentize(`${c}.200`, 0.12)(theme)
  const darkActiveBg = transparentize(`${c}.200`, 0.24)(theme)

  return {
    color: mode(`${c}.600`, `${c}.200`)(props),
    bg: 'transparent',
    _hover: {
      bg: mode(`gray.50`, darkHoverBg)(props),
    },
    _active: {
      bg: mode(`${c}.100`, darkActiveBg)(props),
    },
  }
}

const variantOutline: SystemStyleFunction = (props) => {
  const { colorScheme: c, size = 'md' } = props
  const borderColor = mode(`gray.400`, `whiteAlpha.300`)(props)
  let py = '--ok-sizes-2'

  switch(size) {
    case 'sm': 
      py = '--ok-sizes-1'
      break
    case 'xs':
      py = '--ok-sizes-1'
      break
  }

  return {
    py: `calc(var(${py}) - 1px)`,
    bg: 'white',
    border: '1px solid',
    borderColor: c === 'gray' ? borderColor : 'currentColor',
    '.chakra-button__group[data-attached] > &:not(:last-of-type)': {
      marginEnd: '-1px',
    },
    ...variantGhost(props),
  }
}

type AccessibleColor = {
  bg?: string
  color?: string
  hoverBg?: string
  activeBg?: string
}

/** Accessible color overrides for less accessible colors. */
const accessibleColorMap: { [key: string]: AccessibleColor } = {
  yellow: {
    bg: 'yellow.400',
    color: 'black',
    hoverBg: 'yellow.500',
    activeBg: 'yellow.600',
  },
  cyan: {
    bg: 'cyan.400',
    color: 'black',
    hoverBg: 'cyan.500',
    activeBg: 'cyan.600',
  },
  green: {
    bg: 'green.300',
    color: 'green.900',
    hoverBg: 'green.500',
    activeBg: 'green.600',
  },
}

const variantSolid: SystemStyleFunction = (props) => {
  const { colorScheme: c } = props

  if (c === 'gray') {
    const bg = mode(`gray.100`, `whiteAlpha.200`)(props)

    return {
      bg,
      _hover: {
        bg: mode(`gray.200`, `whiteAlpha.300`)(props),
        _disabled: {
          bg,
        },
      },
      _active: { bg: mode(`gray.300`, `whiteAlpha.400`)(props) },
    }
  }

  const {
    bg = `${c}.500`,
    color = 'white',
    hoverBg = `${c}.600`,
    activeBg = `${c}.700`,
  } = accessibleColorMap[c] ?? {}

  const background = mode(bg, `${c}.200`)(props)

  return {
    bg: background,
    color: mode(color, `gray.800`)(props),
    fontWeight: c === 'primary' || c === 'green' ? 'semibold' : 'normal',
    _hover: {
      bg: mode(hoverBg, `${c}.300`)(props),
      _disabled: {
        bg: background,
      },
    },
    _active: { bg: mode(activeBg, `${c}.400`)(props) },
  }
}

const variants = {
  ghost: variantGhost,
  outline: variantOutline,
  solid: variantSolid,
}

const sizes = {
  lg: {
    h: 'auto',
    minW: 'none',
    fontSize: 'md',
    px: 6,
    py: 4,
  },
  md: {
    h: 'auto',
    minW: 'none',
    fontSize: 'md',
    px: 3,
    py: 2,
  },
  sm: {
    h: 'auto',
    minW: 'none',
    fontSize: 'sm',
    px: 2,
    py: 1,
  },
  xs: {
    h: 'auto',
    minW: 'none',
    fontSize: 'xs',
    px: 2,
    py: 0,
  },
}

const defaultProps = {
  variant: 'solid',
  size: 'md',
  colorScheme: 'gray',
}

export const Button = {
  baseStyle,
  variants,
  sizes,
  defaultProps,
}
